import { Component, OnInit, OnChanges, Input } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { TranslocoModule } from '@jsverse/transloco';
import { TRANSLOCO_SCOPE } from '@jsverse/transloco';
import { TranslationService } from 'src/app/services/translation.service';
import {
  QaiTableComponent,
  TableRow,
  HeaderConfig,
  QaiItemGridComponent,
  QaiLoadingComponent,
  SortDirection,
} from '@qai/ui';
import { CommonModule } from '@angular/common';
import { IUserAssignmentResponse } from 'src/app/interfaces/iuser-assignment-response';
import { LocaleService } from 'src/app/services/locale.service';
import { IUserAssignmentMetricsResponse } from 'src/app/interfaces/iuser-assignment-metrics-response';
@Component({
  selector: 'app-practice-tab',
  templateUrl: './practice-tab.component.html',
  styleUrls: ['./practice-tab.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    QaiTableComponent,
    QaiItemGridComponent,
    QaiLoadingComponent,
  ],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'reportingV2',
    },
  ],
})
export class PracticeTabComponent implements OnInit, OnChanges {
  @Input() assignments: IUserAssignmentResponse[] = [];
  @Input() isLoading: boolean = false;
  @Input() userAssignmentMetrics: IUserAssignmentMetricsResponse = {
    percentCertifiedFirstTry: 0,
    avgCertificationScore: 0,
    totalTimeCertifyingSeconds: 0,
    totalCertificationAssignments: 0,
    totalSessions: 0,
    averageSessionsPerAssignment: 0,
    assignmentsEngagedWith: 0,
    averageImprovementPercent: 0
  };

  isTranslationLoaded = false;
  currentPage = 1;

  headers: HeaderConfig[] = [];
  tableData: TableRow[] = [];

  initialSortColumn: string = '';
  initialSortDirection: SortDirection = 'desc';

  // Future date used for sorting null practice dates
  private readonly FUTURE_DATE_FOR_NULL = '9999-12-31T23:59:59.999Z';

  practiceAssignments: IUserAssignmentResponse[] = [];
  allPracticeAssignments: IUserAssignmentResponse[] = [];

  averageSessionsPerAssignment: number = 0;
  assignmentsEngagedRatio: string = '0/0';
  averageImprovementPercent: number = 0;
  practiceAssignmentCount: number = 0;

  constructor(
    private translocoService: TranslocoService,
    private translationService: TranslationService,
    protected localeService: LocaleService
  ) {}

  ngOnInit(): void {
    this.translationService
      .loadScopedTranslation('reportingV2')
      .then(() => {
        this.isTranslationLoaded = true;
        this.initializeTable();
        this.updateTableData();
      })
      .catch((error) => {
        this.isTranslationLoaded = false;
      });
  }

  ngOnChanges(): void {
    if (this.isTranslationLoaded) {
      this.updateTableData();
    }
    this.updateMetrics();
  }

  private updateTableData(): void {
    this.practiceAssignments = this.assignments || [];
    this.tableData = this.createTableRows(this.practiceAssignments);
  }

  private updateMetrics(): void {
    this.averageSessionsPerAssignment = this.userAssignmentMetrics?.averageSessionsPerAssignment ?? 0;

    const engaged = this.userAssignmentMetrics?.assignmentsEngagedWith ?? 0;
    const total = this.userAssignmentMetrics?.totalCertificationAssignments ?? 0;
    this.assignmentsEngagedRatio = total > 0 ? `${engaged}/${total}` : (engaged > 0 ? engaged.toString() : '0/0');

    this.averageImprovementPercent = this.userAssignmentMetrics?.averageImprovementPercent ?? 0;

    this.practiceAssignmentCount = this.userAssignmentMetrics?.totalCertificationAssignments ?? 0;
  }

  private initializeTable() {
    this.headers = [
      {
        name: this.translocoService.translate(
          'ASSIGNMENT_NAME',
          {},
          'reportingV2'
        ),
        sortable: true,
        columnWidth: '30%',
      },
      {
        name: this.translocoService.translate('STATUS', {}, 'reportingV2'),
        sortable: true,
        columnWidth: '20%',
      },
      {
        name: this.translocoService.translate(
          'TOTAL_NUMBER_OF_SESSIONS',
          {},
          'reportingV2'
        ),
        sortable: true,
        columnWidth: '25%',
      },
      {
        name: this.translocoService.translate(
          'DATE_OF_LAST_PRACTICE',
          {},
          'reportingV2'
        ),
        sortable: true,
        columnWidth: '25%',
      },
    ];

    this.initialSortColumn = this.translocoService.translate(
      'DATE_OF_LAST_PRACTICE',
      {},
      'reportingV2'
    );
    this.initialSortDirection = 'desc';

    this.tableData = this.createTableRows(this.practiceAssignments);
  }

  private createTableRows(assignments: IUserAssignmentResponse[]): TableRow[] {
    return assignments.map((assignment: IUserAssignmentResponse) => {
      const lastAttemptDate = assignment.lastAttemptDate;
      const hasLastAttemptDate = lastAttemptDate && lastAttemptDate.trim();
      const dateValue = hasLastAttemptDate
        ? lastAttemptDate 
        : this.FUTURE_DATE_FOR_NULL;

      const dateDisplayValue = hasLastAttemptDate
        ? this.localeService.formatTableDate(lastAttemptDate)
        : '-';

      const row: any = {
        id: assignment.assignmentUuid,
        [this.translocoService.translate('ASSIGNMENT_NAME', {}, 'reportingV2')]:
          { type: 'text', value: assignment.assignmentName },
        [this.translocoService.translate('STATUS', {}, 'reportingV2')]: {
          type: 'text',
          value: this.normalizePracticeStatus(assignment.status),
        },
        [this.translocoService.translate(
          'TOTAL_NUMBER_OF_SESSIONS',
          {},
          'reportingV2'
        )]: { type: 'text', value: assignment.attemptCount.toString() },
        [this.translocoService.translate(
          'DATE_OF_LAST_PRACTICE',
          {},
          'reportingV2'
        )]: {
          type: 'text',
          value: dateDisplayValue,
          sortValue: dateValue
        },
      };
      return row as TableRow;
    });
  }

  onPageChanged(event: any): void {
    this.currentPage = event.page;
  }

  normalizePracticeStatus(status: string): string {
    // SAFE: compares against hardcoded internal status literals
    const normalizedStatus = status.toLowerCase();
    // Backend returns: NOT_STARTED, IN_PROGRESS, COMPLETED (uppercase)
    // Map to: Assigned, Engaged, Proficient
    switch (normalizedStatus) {
      case 'not_started':
        return this.translocoService.translate('reportingV2.ASSIGNED_LABEL');
      case 'in_progress':
        return this.translocoService.translate('reportingV2.IN_PROGRESS_LABEL');
      case 'completed':
        return this.translocoService.translate('reportingV2.PROFICIENT_LABEL');
      default:
        return status;
    }
  }
}
